/* Auth context — provides current user state and login/logout to the app. */
import { createContext, useContext, useState, useEffect } from "react";
import { api, clearAuthStorage } from "../utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      api.get("/api/auth/me")
        .then(setUser)
        .catch(() => { clearAuthStorage(); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const data = await api.post("/api/auth/login", { email, password });
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    // Re-fetch /me so we get is_report_reviewer (login response doesn't include it)
    const me = await api.get("/api/auth/me");
    setUser(me);
    return data;
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
  };

  const impersonate = async (userId) => {
    // Save current admin access + refresh so we can restore both when
    // stopImpersonating fires. (audit finding A10 — previously only
    // access was stashed, so exiting impersonation left the admin
    // without a working refresh token and they'd get bounced out on
    // the next 15-min expiry.)
    const currentAccess = localStorage.getItem("access_token");
    const currentRefresh = localStorage.getItem("refresh_token");
    const data = await api.post("/api/auth/impersonate", { user_id: userId });
    localStorage.setItem("original_token", currentAccess);
    if (currentRefresh) localStorage.setItem("original_refresh_token", currentRefresh);
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("impersonating", "true");
    window.location.reload();
  };

  const stopImpersonating = () => {
    const originalToken = localStorage.getItem("original_token");
    const originalRefresh = localStorage.getItem("original_refresh_token");
    if (originalToken) {
      localStorage.setItem("access_token", originalToken);
      if (originalRefresh) {
        localStorage.setItem("refresh_token", originalRefresh);
      } else {
        localStorage.removeItem("refresh_token");
      }
      localStorage.removeItem("original_token");
      localStorage.removeItem("original_refresh_token");
      localStorage.removeItem("impersonating");
      window.location.reload();
    } else {
      // No `original_token` — the banner is driven by the separate
      // "impersonating" key, so the two can disagree (cleared storage, a
      // half-finished login, an older tab). Without this branch the button
      // did nothing at all, forever, with no message: the admin stayed on
      // someone else's session believing they had left it.
      //
      // There is deliberately no toast: the admin identity cannot be
      // recovered without the original token, so the only correct outcome
      // is a fresh login, and a toast raised immediately before a full page
      // navigation is never painted. `clearAuthStorage` drops the
      // impersonated tokens too — staying signed in as someone else would
      // be the worse half of this bug. (features-incomplete audit F50)
      clearAuthStorage();
      window.location.href = "/login";
    }
  };

  // Impersonation flag lives in localStorage so it survives reloads.
  // Backed by state + a `storage` listener so another tab starting or
  // stopping impersonation flips the banner in this tab too (was a
  // plain render-time read before, which never re-evaluated).
  // (audit 2026-07-23)
  const [isImpersonating, setIsImpersonating] = useState(
    () => localStorage.getItem("impersonating") === "true"
  );
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "impersonating" || e.key === null) {
        setIsImpersonating(localStorage.getItem("impersonating") === "true");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const hasRole = (...roles) => user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasRole, impersonate, stopImpersonating, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
